import clsx from 'clsx';
import isUndefined from 'lodash/isUndefined';
import React from 'react';
import SwipeableViews from 'react-swipeable-views';
import { bindKeyboard, virtualize } from 'react-swipeable-views-utils';

import { Image, ShowroomSectionOptions } from 'generated/graphql';

import { getImageUrl, getVersion } from 'lib/cloudinary';
import { useGetCurrentShowroomData } from 'lib/showroom';

import AppBar from '@material-ui/core/AppBar';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import ClearIcon from '@material-ui/icons/Clear';

import ShowroomProductLayer from './Product';
import ShowroomZoom from './Zoom';
import { useStyles } from './styles';

export type ComponentProps = {
	sectionOptions: ShowroomSectionOptions;
	imageFiles: Image[];
	open: boolean;
	showLabel: boolean;
	initialIndex?: number;
	onImageChange?(id: string): void;
	onClose(): void;
};

const VirtualizedSwipeableViews = bindKeyboard(virtualize(SwipeableViews));

const ShowroomLayer = (props: ComponentProps) => {
	const { sectionOptions, imageFiles, open, showLabel, initialIndex, onImageChange, onClose } = props;

	const classes = useStyles();

	const [currentImage, setCurrentImage] = React.useState(0);
	const showroom = useGetCurrentShowroomData();

	const logo = showroom.library?.logo;

	const showroomLogo = React.useMemo(
		() =>
			logo?.publicId && logo?.publicUrl
				? getImageUrl(logo.publicId, {
						width: 315,
						crop: 'scale',
						version: getVersion(logo.publicUrl)
				  })
				: `${process.env.PUBLIC_URL}/logo.svg`,
		[logo]
	);

	React.useEffect(() => {
		if (!isUndefined(initialIndex) && open) setCurrentImage(initialIndex);
	}, [initialIndex, open]);

	const handlePrev = React.useCallback(() => {
		const index = currentImage === 0 ? imageFiles.length - 1 : currentImage - 1;
		setCurrentImage(index);
		if (onImageChange) onImageChange(imageFiles[index].cloudinaryId);
	}, [currentImage, imageFiles, onImageChange]);

	const handleNext = React.useCallback(() => {
		const index = currentImage === imageFiles.length - 1 ? 0 : currentImage + 1;
		setCurrentImage(index);
		if (onImageChange) onImageChange(imageFiles[index].cloudinaryId);
	}, [currentImage, imageFiles, onImageChange]);

	const onExitedHanlder = React.useCallback(() => {
		setCurrentImage(0);
	}, []);

	const imageLayerRenderer = React.useCallback(
		({ index }) => {
			const key = `showroomSwipeableView_${imageFiles[index]?.id}`;
			if (sectionOptions.zoomLayer) return <ShowroomZoom file={imageFiles[index]} index={index} key={key} />;
			else if (sectionOptions.productLayer)
				return <ShowroomProductLayer file={imageFiles[index]} index={index} sectionOptions={sectionOptions} key={key} />;
			else return null;
		},
		[imageFiles, sectionOptions]
	);

	return (
		<Dialog id="showroomContentLayerDialog" fullScreen open={open} onClose={onClose} onExited={onExitedHanlder}>
			<DialogTitle className={classes.dialogTitle} disableTypography={true}>
				<AppBar className={classes.previewHeader} position="static">
					<Toolbar className={clsx(classes.zoomToolbar)}>
						<div className={clsx(classes.zoomLogo, { [classes.fullLogo]: !showLabel })}>
							<img id="showroomDialogLayerLogo" src={showroomLogo} className={classes.zoomLogoImg} alt="" />
						</div>
						{showLabel && imageFiles[currentImage] ? (
							<Typography id="showroomDialogLayer" className={classes.currentImageLabel} variant="h5">
								{imageFiles[currentImage].name}
							</Typography>
						) : null}
						<IconButton id="showroomDialogPrevButton" onClick={handlePrev} className={classes.showroomZoomArrowButton} disableRipple>
							<ArrowBackIosIcon viewBox="-4 0 24 24" className={classes.showroomZoomArrowIcon} />
						</IconButton>
						<IconButton id="showroomDialogNextButton" onClick={handleNext} className={classes.showroomZoomArrowButton} disableRipple>
							<ArrowForwardIosIcon viewBox="-2 0 24 24" className={classes.showroomZoomArrowIcon} />
						</IconButton>
						<IconButton id="showroomDialogCloseButton" onClick={onClose} className={classes.showroomDialogCloseButton} disableRipple>
							<ClearIcon className={classes.showroomZoomCloseIcon}></ClearIcon>
						</IconButton>
					</Toolbar>
				</AppBar>
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				<VirtualizedSwipeableViews
					id={'showroomContentSwipeableViews'}
					index={currentImage}
					enableMouseEvents
					onChangeIndex={index => {
						setCurrentImage(index);
						if (onImageChange) onImageChange(imageFiles[index]?.cloudinaryId);
					}}
					slideCount={imageFiles.length}
					className={classes.swipeableWrapper}
					containerStyle={{ height: '100%', alignItems: 'center' }}
					slideClassName={classes.swipeableSlide}
					slideRenderer={imageLayerRenderer}
				/>
			</DialogContent>
		</Dialog>
	);
};

export default React.memo(ShowroomLayer);
